import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ScoringRow from './ScoringRow.tsx';
import ScoringService from '../../api_handlers/services/ScoringService.ts';
import { useAppSelector } from '../../redux/hook.ts';
import { useToast } from '../base_components/ToastContext.tsx';
import BonusRow from './BonusRow.tsx';
import SessionService from '../../api_handlers/services/SessionService.ts';
import IInitializeSessionVM from '../../api_handlers/view_modals/IInitializeSessionVM.ts';

const ScoringCardContent = ({ collectibles, facilatorCurrentIndex, fetchScoringCard }) => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const { showToast } = useToast();
  const [isFading, setIsFading] = useState(false);
  const [standardCredits, setStandardCredits] = useState(0);
  const [bonusRows, setBonusRows] = useState([{ key: '', value: 0 }]);

  const session = useAppSelector((state) => state.session.session);
  const user = useAppSelector((state) => state.user.user);

  const sessionId = session?.sessionId;
  const teamId = session?.teamId;
  const userId = session?.userId;

  const indexMapping = {
    0: { collectibleIndex: 0, rowIndex: 0 },
    1: { collectibleIndex: 0, rowIndex: 1 },
    2: { collectibleIndex: 0, rowIndex: 2 },
    3: { collectibleIndex: 1, rowIndex: 0 },
    4: { collectibleIndex: 1, rowIndex: 1 },
    5: { collectibleIndex: 1, rowIndex: 2 },
    6: { collectibleIndex: 2, rowIndex: 0 },
    7: { collectibleIndex: 3, rowIndex: 0 },
    8: { collectibleIndex: 3, rowIndex: 1 },
    9: { collectibleIndex: 3, rowIndex: 2 },
    10: { collectibleIndex: 3, rowIndex: 3 },
    11: { collectibleIndex: 4, rowIndex: 0 },
  };

  const { collectibleIndex = 0, rowIndex = 0 } = indexMapping[facilatorCurrentIndex] || {};
  const currentCollectible = collectibles?.[collectibleIndex] || { rows: [] };
  const currentRow = currentCollectible?.rows?.[rowIndex] || null;

  const handleNext = async () => {
    try {
      if (!currentCollectible) {
        console.error('Current collectible is undefined.');
        return;
      }

      if (currentCollectible.type === 'collectible') {

        await saveCollectibleCount();
        showToast('Collectible Added Successfully. Wait for facilitator to move to the next step.');

      } else if (currentCollectible.type === 'standard_credit') {

        await ScoringService.setCollectibleCount({
          collectibleTypeEnumId: 7,
          sessionId,
          teamId,
          userId,
          quantity: standardCredits,
        });

        showToast('Standard Credit Added');
        const bonuses = await fetchBonuses();
        setBonusRows(bonuses);

      }

    } catch (err) {
      console.error(err);
    }
  };

  const saveCollectibleCount = async () => {
    try {
      if (!currentRow) {
        console.error('Current row is undefined.');
        return;
      }

      const temp = currentRow[Object.keys(currentRow)?.[0]];
      if (!temp) {
        console.error('Temp value is undefined.');
        return;
      }

      await ScoringService.setCollectibleCount({
        collectibleTypeEnumId: temp.id,
        sessionId,
        teamId,
        userId,
        quantity: counts[temp.id],
      });
    } catch (err) {
      console.error(err);
    }
  };

  const calculateSubtotal = (quantity, id) => {
    setCounts((prevCounts) => ({
      ...prevCounts,
      [id]: quantity,
    }));
    if (id === 7) {
      setStandardCredits(quantity);
    }
  };

  const fetchBonuses = useCallback(async () => {
    try {
      const data = await ScoringService.fetchBonuses({ sessionId, teamId });
      return data?.data?.bonuses || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  }, [sessionId, teamId]);

  const saveBonuses = useCallback(async () => {
    try {
      await ScoringService.saveBonuses({ sessionId, teamId });
      showToast('Bonus Added');
    } catch (err) {
      console.error(err);
    }
  }, [sessionId, teamId, showToast]); 

  const redirectToVoting = useCallback(async () => {
    try {
      const vm: IInitializeSessionVM = {
        userEmail: user?.email || '',
        userName: user?.userName || '',
        teamId: session?.teamId || 0,
        gaminarCode: session?.gaminarCode || '',
      };

      await SessionService.initializeVotingSession(vm);
      navigate('/LandingPage');
    } catch (error) {
      // Handle error here if needed
      console.error("Error initializing voting session:", error);
    }
  }, [session, user, navigate]);

  useEffect(() => {
    fetchBonuses().then((res) => {
      setBonusRows(res);
    });
  }, [fetchBonuses]);

  useEffect(() => {
    // Check fasilatorCurrentIndex and call the necessary functions
    if (facilatorCurrentIndex === 12) {
      redirectToVoting();
    }
    if (facilatorCurrentIndex === 10) {
      saveBonuses();
    }
    fetchScoringCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilatorCurrentIndex, redirectToVoting]);

  useEffect(() => {
    setIsFading(true);
    const timeout = setTimeout(() => setIsFading(false), 500);
    return () => clearTimeout(timeout);
  }, [rowIndex]);

  return (
    <Box display="flex" justifyContent="space-between" flexDirection="column" sx={{ width: '100%', overflow: 'hidden' }}>
      <Box
        display="flex"
        overflow="visible"
        width="100%"
        position="relative"
        sx={{
          px: { xs: 0, sm: 0 },
          minHeight: { xs: '28rem', md: '26rem' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {collectibles.map((collectible, index) => (
          <Card
            variant="outlined"
            key={collectible.id}
            sx={{
              minWidth: '100%',
              maxWidth: '100%',
              position: 'absolute',
              top: '50%',
              left: 0,
              transform: `translate(${collectibleIndex === index ? '0' : collectibleIndex > index ? '-100%' : '100%'}, -50%)`,
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              height: 'auto',
              opacity: isFading ? 0 : 1,
              transition: 'transform 0.5s ease, opacity 0.5s ease-in-out',
              visibility: collectibleIndex === index ? 'visible' : 'hidden',
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                padding: { xs: 0.5, sm: 1, md: 2 },
                '&:last-child': { paddingBottom: { xs: 0.5, sm: 1, md: 2 } },
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              {collectible.type.includes('collectible') && currentRow && (
                <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 0.5, sm: 1 } }}>
                  <ScoringRow
                    item={currentRow[Object.keys(currentRow)?.[0]]}
                    onValueChange={calculateSubtotal}
                  />
                </Box>
              )}

              {collectible.type.includes('standard_credit') && (
                <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 0.5, sm: 1 } }}>
                  <ScoringRow
                    item={collectible}
                    onValueChange={calculateSubtotal}
                    isStandardCredit
                  />
                </Box>
              )}

              {collectible.type.includes("bonus") && (
                <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 1, sm: 2 } }}>
                  <BonusRow
                    bonus={bonusRows[rowIndex]}
                    index={rowIndex}
                  />
                </Box>
              )}

              {collectible.type.includes('final_score') && (
                <Box
                  sx={{
                    textAlign: 'center',
                    padding: { xs: '20px', sm: '26px', md: '32px' },
                    background: 'linear-gradient(135deg, rgba(218, 165, 32, 0.6) 0%, rgba(184, 134, 11, 0.8) 100%)',
                    borderRadius: { xs: '12px', md: '16px' },
                    border: { xs: '2px solid rgba(255, 215, 0, 0.5)', md: '3px solid rgba(255, 215, 0, 0.5)' },
                    boxShadow: '0 0 30px rgba(218, 165, 32, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.1)',
                    mx: { xs: 1, sm: 2 },
                    width: 'calc(100% - 16px)',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                  className="animate-scale-in"
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: "'Philosopher', sans-serif",
                      color: '#FFD700',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                      marginBottom: 2,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    }}
                  >
                    Your Final Score
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '48px', sm: '56px', md: '64px' },
                      fontFamily: "'Cinzel', serif",
                      color: '#FFF',
                      textShadow: '4px 4px 8px rgba(0,0,0,0.9), 0 0 20px rgba(255, 215, 0, 1)',
                      background: 'linear-gradient(180deg, #FFD700 0%, #FFA500 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.5))',
                      lineHeight: 1,
                    }}
                    className="animate-count-up"
                  >
                    {collectible.value}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 2,
        }}
      >
        {facilatorCurrentIndex < 7 && (
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{
              padding: { xs: '0.75rem 2rem', md: '0.75rem 2rem' },
              fontSize: { xs: '1rem', md: '1.125rem' },
              fontFamily: "'Cinzel', serif",
              fontWeight: 700,
              background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
              border: '0.125rem solid rgba(255, 215, 0, 0.6)',
              borderRadius: '0.75rem',
              boxShadow: '0 0.25rem 1rem rgba(218, 165, 32, 0.4), inset 0 0.0625rem 0 rgba(255, 255, 255, 0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.0625rem',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)',
                boxShadow: '0 0.375rem 1.25rem rgba(218, 165, 32, 0.6), inset 0 0.0625rem 0 rgba(255, 255, 255, 0.4)',
                transform: 'translateY(-0.125rem)',
              },
              '&:active': {
                transform: 'translateY(0)',
              }
            }}
          >
            Submit
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ScoringCardContent;
